import { useEffect, useState } from "react";
import axios from "axios";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { MaskedLines, Reveal, Eyebrow } from "../components/motion";
import { useProducts } from "../hooks/useProducts";
import { API_URL } from "../config/site";

const Stars = ({ value, onChange, testIdPrefix }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        data-testid={onChange ? `${testIdPrefix}-star-${n}` : undefined}
        onClick={onChange ? () => onChange(n) : undefined}
        className={onChange ? "cursor-pointer" : "cursor-default"}
        aria-label={`${n} stelle`}
      >
        <Star className={`w-5 h-5 ${n <= value ? "text-lilac fill-lilac" : "text-lilac-light"}`} />
      </button>
    ))}
  </div>
);

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 5, text: "", product_slug: "" });
  const [sending, setSending] = useState(false);
  const { products } = useProducts();

  useEffect(() => {
    axios.get(`${API_URL}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/reviews`, {
        name: form.name,
        rating: form.rating,
        text: form.text,
        product_slug: form.product_slug || null,
      });
      toast.success(data.message);
      setForm({ name: "", rating: 5, text: "", product_slug: "" });
    } catch {
      toast.error("Controlla i campi e riprova.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="reviews-page" className="py-14 sm:py-20">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center">
          <Reveal>
            <Eyebrow className="justify-center flex items-center gap-2">
              <Star className="w-4 h-4 fill-matcha" /> Le vostre esperienze
            </Eyebrow>
          </Reveal>
          <MaskedLines
            as="h1"
            delay={0.1}
            lines={["Le parole di chi", "ha iniziato il percorso"]}
            className="mt-4 font-serif text-4xl sm:text-5xl text-plum tracking-tight leading-[1.12]"
          />
          <Reveal delay={0.4}>
            <p className="mt-5 text-base sm:text-lg text-plum/60 max-w-xl mx-auto leading-relaxed">
              Oltre 53 donne hanno già scelto il Trattamento Rapida Crescita e hanno condiviso la loro esperienza.
            </p>
          </Reveal>
        </div>

        <div className="mt-12 space-y-5">
          {reviews.length === 0 ? (
            <Reveal>
              <div className="bg-mist rounded-3xl p-10 text-center" data-testid="reviews-empty-state">
                <p className="font-serif text-xl text-plum">Le recensioni verificate appariranno qui</p>
                <p className="mt-2 text-sm text-plum/50 max-w-md mx-auto">
                  Pubblichiamo solo esperienze reali delle nostre clienti, dopo una breve verifica.
                  Nessuna recensione inventata: solo la vostra voce.
                </p>
              </div>
            </Reveal>
          ) : (
            reviews.map((r, i) => (
              <Reveal key={r.id || i} delay={i * 0.06}>
                <div className="bg-mist rounded-3xl p-7" data-testid={`review-card-${i}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-plum">{r.name}</p>
                    <Stars value={r.rating} />
                  </div>
                  <p className="mt-3 text-sm sm:text-base text-plum/70 leading-relaxed">{r.text}</p>
                </div>
              </Reveal>
            ))
          )}
        </div>

        <Reveal className="mt-14">
          <div className="bg-lilac-soft border border-lilac-light rounded-[2.5rem] p-8 sm:p-12">
            <h2 className="font-serif text-2xl sm:text-3xl text-plum tracking-tight text-center">
              Hai provato i nostri prodotti?
            </h2>
            <p className="mt-3 text-sm sm:text-base text-plum/60 text-center max-w-md mx-auto">
              Lascia la tua esperienza: sarà pubblicata dopo una breve verifica.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-4 max-w-lg mx-auto" data-testid="review-form">
              <input
                data-testid="review-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Il tuo nome o le tue iniziali"
                className="w-full bg-white rounded-2xl px-5 py-4 text-sm text-plum outline-none border border-transparent focus:border-lilac-dark transition-colors"
              />
              <select
                data-testid="review-product-select"
                value={form.product_slug}
                onChange={(e) => setForm({ ...form, product_slug: e.target.value })}
                className="w-full bg-white rounded-2xl px-5 py-4 text-sm text-plum outline-none border border-transparent focus:border-lilac-dark transition-colors"
              >
                <option value="">Quale prodotto hai provato? (facoltativo)</option>
                {products.map((p) => (
                  <option key={p.slug} value={p.slug}>{p.name}</option>
                ))}
              </select>
              <div className="flex items-center gap-3 bg-white rounded-2xl px-5 py-4">
                <span className="text-sm text-plum/60">La tua valutazione</span>
                <Stars value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} testIdPrefix="review" />
              </div>
              <textarea
                data-testid="review-text-input"
                required
                minLength={10}
                rows={4}
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="Raccontaci la tua esperienza..."
                className="w-full bg-white rounded-2xl px-5 py-4 text-sm text-plum outline-none border border-transparent focus:border-lilac-dark transition-colors resize-none"
              />
              <button
                data-testid="review-submit-btn"
                disabled={sending}
                className="w-full py-4 rounded-full bg-lilac text-white font-bold text-sm uppercase tracking-[0.14em] hover:bg-lilac-dark active:scale-[0.98] transition-all duration-300 disabled:opacity-60"
              >
                {sending ? "Invio in corso..." : "Invia la tua recensione"}
              </button>
            </form>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
